import { batch } from 'react-redux';
import { tagConstants } from '../_constants';
import { tagService } from '../_services';
import { stackUtilities } from '../_utilities';
import { alertActions, stackActions, boxActions } from './';
import { store } from '../store';

export const tagActions = {
  select,
  deselect,
  tag,
  untag,
  load,
  updateTagItem,
};

// Whether a tag request for this scan and tag is still waiting on the server,
// read from the live store. The handlers use it to decide whether an untag
// cancels a placeholder or deletes a real link. They used to read tag.tagQueue
// from their render, but the queue is replaced on every request and answer
// (BZL-191), and the space hotkey keeps the previous render's closure until a
// passive effect rebinds it -- so a press as the server answered sent the
// placeholder, and the link the user removed stayed on the server (BZL-207).
export function tagInFlight(id, stackId) {
  const entry = store.getState().tag.tagQueue[`${id}+${stackId}`];
  return !!entry && entry.type === tagConstants.TAG_REQUEST;
}

// Where object is the byId dictionary, and array is allIds (normalized data)
function select(name, id, object, array) {
  const items = stackUtilities.filter(
    id,
    object,
    array,
    'stackId',
    'origamiId'
  );
  return { type: tagConstants.SELECT, name, id, items };
}

// Clears the auto tag. Until this existed a set tag could only be replaced by
// another one; the only way back to "Select" was a page reload. Returns the
// same shape as initialTagState so the bottom bar and every isAutoTag check
// read it as "nothing set".
function deselect() {
  return { type: tagConstants.SELECT, name: null, id: null, items: [] };
}

// Used to replace tagConstants.TAG_PENDING placeholder until id is returned from API
function updateTagItem(link, items) {
  items[link.origamiId] = link.id;
  return { type: tagConstants.TAG_PENDING, items };
}

function untag(id, linkId, stackId, items) {
  const queueId = `${id}+${stackId}`;
  // Filled in when this thunk is dispatched, not when it is built. Reading the
  // queue at build time and mutating that object meant a thunk built before
  // another request's success, and dispatched after it, carried a queue the
  // store had already moved past -- so dispatching it put the completed entry
  // back, and a resurrected UNTAG_REQUEST makes the next tag's success take its
  // kill branch and delete the link the user just asked for (BZL-191).
  let tagQueue = null;
  let nextQueue = null;

  return (dispatch) => {
    // Doesn't exist, cannot remove
    if (items && !items.hasOwnProperty(id)) {
      dispatch(failure('Tag does not exist.'));
      dispatch(alertActions.error('Tag does not exist.'));
    } else {
      // Optimistic UI update
      if (items !== null && items.hasOwnProperty(id)) {
        delete items[id];
      }

      // Read the live queue here, and replace rather than mutate it: the
      // reducer swaps the whole object on success, so the only safe time to
      // look is the moment this request is actually being made.
      const liveQueue = store.getState().tag.tagQueue;
      const queueIndex = liveQueue.hasOwnProperty(queueId)
        ? liveQueue[queueId].index + 1
        : 0;

      tagQueue = {
        ...liveQueue,
        [queueId]: {
          index: queueIndex,
          type: tagConstants.UNTAG_REQUEST,
        },
      };

      batch(() => {
        dispatch(request());
        dispatch(stackActions.deleteLink({ id: linkId, stackId: stackId }));
        dispatch(
          boxActions.deleteLink({ id: linkId, origamiId: id, stackId: stackId })
        );
      });

      if (linkId === tagConstants.TAG_REQUEST) {
        dispatch(canceled());
      } else {
        tagService
          .untag(linkId)
          .then((res) => {
            //const link = res.data.updateOrigamiLink;
            const liveQueue = store.getState().tag.tagQueue;
            const { [queueId]: ours, ...rest } = liveQueue;

            // Drop our own entry and leave every other pair's alone. Clearing
            // the whole queue deleted the entries of requests still in flight,
            // and the next one back then found nothing under its own queueId.
            // A missing entry means the same thing as our own: nothing is
            // queued behind us for this pair.
            if (!ours || ours.type === tagConstants.UNTAG_REQUEST) {
              nextQueue = rest; // was the last in queue
            } else {
              nextQueue = liveQueue;
            }

            dispatch(success());
          })
          .catch((error) => {
            dispatch(failure(error.toString()));
            dispatch(alertActions.error(error.toString()));
          });
      }
    }
  };

  function request() {
    return { type: tagConstants.UNTAG_REQUEST, items, tagQueue };
  }
  function success() {
    return { type: tagConstants.UNTAG_SUCCESS, nextQueue };
  }
  function canceled() {
    return { type: tagConstants.UNTAG_CANCELED };
  }
  function failure(error) {
    return { type: tagConstants.UNTAG_FAILURE };
  }
}

function tag(id, stackId, items) {
  const queueId = `${id}+${stackId}`;
  // See untag(): filled in at dispatch time, never at build time (BZL-191).
  let tagQueue = null;
  let nextQueue = null;

  return (dispatch) => {
    // Already exists, do not add extra tag
    if (items && items[id] === stackId) {
      dispatch(failure('Tag already exists.'));
      dispatch(alertActions.error('Tag already exists.'));
    } else {
      // Optimistic UI update, update both items for checkmark & linklist for #
      if (items !== null) {
        items[id] = tagConstants.TAG_REQUEST;
      }

      const liveQueue = store.getState().tag.tagQueue;
      const queueIndex = liveQueue.hasOwnProperty(queueId)
        ? liveQueue[queueId].index + 1
        : 0;

      tagQueue = {
        ...liveQueue,
        [queueId]: {
          index: queueIndex,
          type: tagConstants.TAG_REQUEST,
        },
      };

      // The stack and box stores are written by separate dispatches, and
      // everything that resolves an origami's tags reads both. React 17 only
      // batches renders inside its own event handlers; a hotkey (A19) or a
      // resolved promise is not one, so without batch() each dispatch renders
      // on its own and the sidebar sees the stores mid-update -- the tag
      // drops out for a frame between the two writes.
      batch(() => {
        dispatch(request());
        dispatch(
          stackActions.addLink({
            id: tagConstants.TAG_REQUEST,
            stackId: stackId,
            origamiId: id,
          })
        );
        dispatch(
          boxActions.addLink({
            id: tagConstants.TAG_REQUEST,
            origamiId: id,
            stackId: stackId,
          })
        );
      });

      tagService
        .tag(id, stackId)
        .then((res) => {
          const link = res.data.createOrigamiLink;
          const liveQueue = store.getState().tag.tagQueue;
          const { [queueId]: ours, ...rest } = liveQueue;

          // See untag(): our own entry goes, everyone else's stays, and a
          // missing entry means nothing is queued behind us. Taking the kill
          // branch on a missing entry would delete the tag the user asked for.
          if (!ours || ours.type === tagConstants.TAG_REQUEST) {
            // Add new link to currently selected tag list
            if (items !== null) {
              items[id] = link.id;
            }
            nextQueue = rest; // was the last in queue
            batch(() => {
              dispatch(success());
              dispatch(stackActions.addLink(link));
              dispatch(boxActions.addLink(link));
            });
          } else {
            // if UNTAG_REQUEST in queue then kill tag
            dispatch(untag(id, link.id, stackId, null));
          }
        })
        .catch((error) => {
          dispatch(failure(error.toString()));
          dispatch(alertActions.error(error.toString()));
        });
    }
  };

  function request() {
    return { type: tagConstants.TAG_REQUEST, items, tagQueue };
  }
  function success() {
    return { type: tagConstants.TAG_SUCCESS, items, nextQueue };
  }
  function failure(error) {
    return { type: tagConstants.TAG_FAILURE };
  }
}

function load(id) {
  return (dispatch) => {
    dispatch(request());

    tagService
      .load(id)
      .then((res) => {
        dispatch(success(res.data.getStack));
      })
      .catch((error) => {
        dispatch(failure(error.toString()));
        dispatch(alertActions.error(error.toString()));
      });
  };

  function request() {
    return { type: tagConstants.STACK_REQUEST };
  }
  function success(data) {
    return { type: tagConstants.STACK_SUCCESS, data };
  }
  function failure(error) {
    return { type: tagConstants.STACK_FAILURE };
  }
}
